import React, { useEffect, useState, useCallback } from 'react';
import api from '../utils/api';
import { Btn, Badge, Modal, Input, Select, Loading, EmptyState } from '../components/ui';

export default function DivisionMaster() {
  const [divisions, setDivisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', code: '', status: 'Active' });
  const [error, setError] = useState('');

  useEffect(() => { document.getElementById('lc-page-title').textContent = 'Division master'; }, []);

  const fetch = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get('/divisions'); setDivisions(data); }
    catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const openAdd = () => { setEditing(null); setForm({ name: '', code: '', status: 'Active' }); setError(''); setModalOpen(true); };
  const openEdit = (d) => { setEditing(d); setForm({ name: d.name, code: d.code, status: d.status }); setError(''); setModalOpen(true); };

  const handleRemove = async (d) => {
    if (!window.confirm(`Remove division "${d.name}"? This cannot be undone.`)) return;
    try { await api.delete(`/divisions/${d.id}`); fetch(); }
    catch (e) { alert(e.response?.data?.error || 'Error removing division'); }
  };

  const handleSave = async () => {
    if (!form.name || !form.code) { setError('Name and code are required'); return; }
    try {
      if (editing) await api.put(`/divisions/${editing.id}`, form);
      else await api.post('/divisions', form);
      setModalOpen(false); fetch();
    } catch (e) { setError(e.response?.data?.error || 'Error saving'); }
  };

  return (
    <div style={{ padding: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 500 }}>Division master <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--text-secondary)', marginLeft: 6 }}>{divisions.length} records</span></div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Top-level organisational units</div>
        </div>
        <Btn variant="primary" size="sm" onClick={openAdd}>+ Add new</Btn>
      </div>

      <div style={{ border: '0.5px solid var(--border-md)', borderRadius: 10, overflow: 'hidden', background: 'var(--bg-primary)' }}>
        {loading ? <Loading /> : divisions.length === 0 ? <EmptyState message="No divisions yet" action={<Btn variant="primary" onClick={openAdd}>+ Add division</Btn>} /> : (
          <table className="lc-table">
            <thead><tr><th>Division name</th><th>Code</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {divisions.map(d => (
                <tr key={d.id}>
                  <td style={{ fontWeight: 500 }}>{d.name}</td>
                  <td><span style={{ fontFamily: 'monospace', fontSize: 12, background: 'var(--bg-secondary)', padding: '2px 6px', borderRadius: 4 }}>{d.code}</span></td>
                  <td><Badge type={d.status === 'Active' ? 'green' : 'red'}>{d.status}</Badge></td>
                  <td style={{ display: 'flex', gap: 12 }}>
                    <a style={{ fontSize: 11, color: 'var(--acc)', cursor: 'pointer' }} onClick={() => openEdit(d)}>Edit</a>
                    <a style={{ fontSize: 11, color: 'var(--red)', cursor: 'pointer' }} onClick={() => handleRemove(d)}>Remove</a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editing ? 'Edit division' : 'Add division'} width={360}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Division name</div>
            <Input value={form.name} onChange={v => setForm(p => ({ ...p, name: v }))} placeholder="e.g. South India" style={{ width: '100%' }} />
          </div>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Division code <span style={{ color: 'var(--text-tertiary)' }}>(max 10 chars)</span></div>
            <Input value={form.code} onChange={v => setForm(p => ({ ...p, code: v.slice(0, 10).toUpperCase() }))} placeholder="e.g. SOUTH" style={{ width: '100%' }} />
          </div>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Status</div>
            <Select value={form.status} onChange={v => setForm(p => ({ ...p, status: v }))} options={[{ value: 'Active', label: 'Active' }, { value: 'Inactive', label: 'Inactive' }]} style={{ width: '100%' }} />
          </div>
          {error && <div style={{ fontSize: 11.5, color: 'var(--red)', background: 'var(--red-bg)', padding: '6px 9px', borderRadius: 6 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
            <Btn onClick={() => setModalOpen(false)}>Cancel</Btn>
            <Btn variant="primary" onClick={handleSave}>Save</Btn>
          </div>
        </div>
      </Modal>
    </div>
  );
}